import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, AlertTriangle, Clock, Calendar, Users, Sparkles, LayoutDashboard, Shield, Headphones, BadgeEuro, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LandingHeader, LandingFooter } from "@/components/landing";
import { allFeatures } from "@/components/product";
import { usePageMeta } from "@/hooks/usePageMeta";

// Arguments différenciants
const whyB8ild = [
  {
    icon: MapPin,
    title: "Made in France",
    description: "Solution 100% française, hébergée en France, conforme RGPD.",
    gradient: "from-[#1A365D] to-[#2563EB]",
  },
  {
    icon: Headphones,
    title: "Support réactif",
    description: "Une équipe à votre écoute, qui comprend vos métiers.",
    gradient: "from-[#10B981] to-[#059669]",
  },
  {
    icon: Shield,
    title: "Simplicité avant tout",
    description: "Interface intuitive pensée pour les professionnels du terrain.",
    gradient: "from-[#F59E0B] to-[#D97706]",
  },
  {
    icon: BadgeEuro,
    title: "Prix transparent",
    description: "Pas de frais cachés. Tarification claire et prévisible.",
    gradient: "from-[#8B5CF6] to-[#7C3AED]",
  },
];

const Product = () => {
  usePageMeta({ title: "Fonctionnalités B8ild — Gestion chantiers BTP", description: "Rentabilité temps réel, jour critique, OCR factures, analyse IA, planning équipes. Toutes les fonctionnalités B8ild pour piloter vos chantiers.", url: "https://b8ild.fr/product" });
  return (
    <div className="min-h-screen bg-[#0F172A]">
      <LandingHeader />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden bg-[#0A0A0F]">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0F] via-[#0F172A] to-[#0A0A0F]" />
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#F59E0B]/8 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#1A365D]/30 rounded-full blur-[120px]" />
        </div>

        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:64px_64px]" />

        <div className="container relative z-10 mx-auto px-4 py-32 pt-40">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2.5 rounded-full border border-white/[0.08] bg-white/[0.03] px-4 py-2 backdrop-blur-sm animate-fade-up mb-8">
              <span className="text-sm font-medium text-[#F59E0B] uppercase tracking-wider">
                Produit
              </span>
            </div>

            {/* Title */}
            <h1 className="animate-fade-up [animation-delay:100ms] mb-6">
              <span className="block text-[2.5rem] sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1]">
                Tout ce qu'il vous faut pour
              </span>
              <span className="block text-[2.5rem] sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mt-2">
                <span className="bg-gradient-to-r from-[#F59E0B] via-[#FBBF24] to-[#F59E0B] bg-clip-text text-transparent bg-[length:200%_auto] animate-shimmer">
                  piloter vos chantiers.
                </span>
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-white/50 leading-relaxed max-w-2xl mx-auto animate-fade-up [animation-delay:200ms] mb-10">
              Une suite complète d'outils pensés pour les professionnels du BTP.
              <br />
              Rentabilité, équipes, factures, alertes — tout en un seul endroit.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up [animation-delay:300ms]">
              <Button
                size="lg"
                className="h-12 px-6 bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F] font-semibold text-base gap-2.5 rounded-xl shadow-[0_0_0_1px_rgba(245,158,11,0.3),0_4px_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_0_1px_rgba(245,158,11,0.5),0_8px_30px_rgba(245,158,11,0.4)] hover:scale-[1.02] transition-all duration-300"
                asChild
              >
                <Link to="/auth?mode=signup">
                  Essayer gratuitement 14 jours
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="ghost"
                className="h-12 px-6 border border-white/[0.08] bg-white/[0.02] text-white/80 hover:text-white hover:bg-white/[0.05] hover:border-white/[0.15] font-medium text-base gap-2.5 rounded-xl transition-all duration-300"
                asChild
              >
                <a href="#features">
                  Voir les fonctionnalités
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="relative py-24 bg-[#0F172A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-wider text-[#F59E0B] font-medium mb-3">
              Fonctionnalités
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              7 outils pour piloter votre rentabilité
            </h2>
            <p className="text-lg text-white/50 max-w-2xl mx-auto">
              Chaque fonctionnalité a été conçue pour répondre aux besoins réels des professionnels du BTP.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {allFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <Link
                  key={feature.id}
                  to={feature.href}
                  className="group rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.1] to-transparent hover:from-[#F59E0B]/30 transition-all duration-300"
                >
                  <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl p-6 h-full min-h-[200px] flex flex-col">
                    <div className={`h-14 w-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg`}>
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-[#F59E0B] transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-white/50 leading-relaxed flex-grow">
                      {feature.description}
                    </p>
                    <div className="flex items-center gap-2 text-sm font-medium text-[#F59E0B] mt-4 group-hover:gap-3 transition-all">
                      En savoir plus
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why B8ild */}
      <section className="relative py-24 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-wider text-[#F59E0B] font-medium mb-3">
              Pourquoi B8ild ?
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              La solution pensée pour votre métier
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {whyB8ild.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.08] to-transparent"
                >
                  <div className="rounded-2xl bg-[#0F0F14]/80 backdrop-blur-xl p-6 h-full text-center">
                    <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mx-auto mb-4`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-white/50 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="relative py-24 bg-[#0F172A] overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-[#F59E0B]/10 to-transparent rounded-full" />
        </div>

        <div className="container relative z-10 mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-[12px] bg-[#131318] border border-white/[0.06] mb-8">
              <Sparkles className="h-6 w-6 text-white/50" strokeWidth={2.5} />
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Prêt à piloter votre rentabilité ?
            </h2>

            <p className="text-lg text-white/50 mb-10 max-w-xl mx-auto">
              Rejoignez les professionnels du BTP qui ont choisi B8ild pour optimiser leurs chantiers.
            </p>

            <Button
              size="lg"
              className="h-14 px-8 bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F] font-semibold text-lg gap-3 rounded-xl shadow-[0_0_0_1px_rgba(245,158,11,0.3),0_4px_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_0_1px_rgba(245,158,11,0.5),0_8px_30px_rgba(245,158,11,0.4)] hover:scale-[1.02] transition-all duration-300"
              asChild
            >
              <Link to="/auth?mode=signup">
                Essayer gratuitement
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>

            <div className="flex flex-wrap items-center justify-center gap-6 mt-10 text-sm text-white/40">
              <div className="flex items-center gap-2">
                <svg className="h-5 w-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>14 jours gratuits</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="h-5 w-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Sans engagement</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="h-5 w-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Support inclus</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default Product;
